import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { assignPatient, searchPatients } from "@/lib/patientDocs";
import { addRosterPatient } from "@/lib/noteClaim";
import { T, patientBits, roleValue, fmtMDY, displayName } from "./pdUtil";

// Create a patient from the document (spec §6). Lives inside the workspace patient card.
// duplicate check (pg-patient-search) → roster-patients → /assign.
const toISO = (v) => {
  const s = String(v || "").trim(); if (!s) return "";
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return s;
};

function DupRow({ p, active, onPick, testid }) {
  const name = displayName(p.full_name || p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim()) || "—";
  return (
    <button type="button" role="radio" aria-checked={active} data-testid={testid} onClick={onPick}
      className="w-full flex items-center gap-3 px-3 rounded-[8px] text-left" style={{ minHeight: 40, border: `1px solid ${active ? T.indigo : T.hair}`, background: active ? "#F4F4FD" : T.card }}>
      <span style={{ width: 14, height: 14, borderRadius: 999, flex: "none", border: `${active ? 4.5 : 1.5}px solid ${active ? T.indigo : T.mute}` }} />
      <span className="min-w-0 flex-1"><span className="block text-[13px] font-semibold truncate" style={{ color: T.ink }}>{name}</span>
        <span className="block text-[12px] truncate" style={{ color: T.sub }}>MRN {p.mrn || "—"} · DOB {fmtMDY(p.dob) || "—"}</span></span>
    </button>
  );
}

const Field = ({ label, req, value, onChange, testid, w, autoFocus }) => (
  <label className="flex flex-col gap-1" style={{ gridColumn: w ? `span ${w}` : undefined }}>
    <span className="text-[11px] font-semibold" style={{ color: T.sub }}>{label}{req ? <span style={{ color: T.red }}> *</span> : null}</span>
    <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} autoFocus={autoFocus}
      style={{ height: 32, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.hair}`, fontSize: 13, outline: "none" }} />
  </label>
);

export function CreatePatientBlock({ analysisId, clientId, extraction, isCard, onCancel, onDone, onOpenDoc, focusFirst }) {
  const pb = patientBits(extraction || {});
  const ex = extraction || {};
  // Lens often reads a single "full name" with no first/last keys — split it so the form prefills.
  const splitName = (full) => {
    const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { first: "", middle: "", last: "" };
    if (parts.length === 1) return { first: parts[0], middle: "", last: "" };
    return { first: parts[0], last: parts[parts.length - 1], middle: parts.slice(1, -1).join(" ") };
  };
  const kFirst = displayName(roleValue(ex, ["first_name", "patient_first_name", "gov_first_name"]));
  const kMiddle = displayName(roleValue(ex, ["middle_name", "patient_middle_name", "gov_middle_name"]));
  const kLast = displayName(roleValue(ex, ["last_name", "patient_last_name", "gov_last_name"]));
  const sn = (!kFirst && !kLast) ? splitName(pb.rawName) : { first: "", middle: "", last: "" };
  const [f, setF] = useState({
    first_name: kFirst || displayName(sn.first),
    middle_name: kMiddle || displayName(sn.middle),
    last_name: kLast || displayName(sn.last),
    dob: pb.dob || "",
    gender: roleValue(ex, ["sex", "gender", "patient_sex", "gov_sex"]),
    street: roleValue(ex, ["address", "street", "address_line_1", "gov_address"]),
    city: roleValue(ex, ["city", "gov_city"]),
    state: roleValue(ex, ["state", "gov_state"]),
    zip: roleValue(ex, ["zip", "zip_code", "postal_code", "gov_zip"]),
    payer_name: roleValue(ex, ["payer_name", "payer", "insurance"]),
    member_id: roleValue(ex, ["member_id", "subscriber_id", "policy_number"]),
    group_number: roleValue(ex, ["group_number", "group", "group_id"]),
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [dupes, setDupes] = useState(null); // duplicate-check result (radio rows)
  const [dupPanel, setDupPanel] = useState(null); // 409 duplicates
  const [pick, setPick] = useState(null); // chosen existing patient (dup check or 409)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Duplicate check on open: last name (or full name) + DOB, merged by MRN.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = [...new Set([f.last_name, pb.name, pb.dob].filter(Boolean))];
        if (!qs.length) { if (alive) setDupes([]); return; }
        const outs = await Promise.all(qs.map((q) => searchPatients({ client_id: clientId, q })));
        const m = new Map();
        outs.forEach((d) => (d?.results || []).forEach((p) => { const k = String(p.mrn || p.chart_id); if (!m.has(k)) m.set(k, p); }));
        if (alive) setDupes([...m.values()]);
      } catch { if (alive) setDupes([]); }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const linkExisting = async (p) => {
    setBusy(true); setError("");
    try {
      await assignPatient(analysisId, { chart_id: p.chart_id || null, chart_uuid: p.chart_uuid || null, mrn: p.mrn || null, full_name: p.full_name || p.name || null, dob: p.dob || null, source: p.source || "postgres" });
      toast.success(`Linked to ${displayName(p.full_name || p.name || "patient")}`);
      onDone?.();
    } catch { setBusy(false); setError("Couldn't link this document. Try again."); }
  };

  const submit = async () => {
    if (pick) return linkExisting(pick);
    if (!f.first_name.trim() || !f.last_name.trim() || !f.dob.trim()) {
      setError("First name, last name and date of birth are needed to create a patient."); return;
    }
    setBusy(true); setError(""); setDupPanel(null);
    const body = {};
    ["first_name", "middle_name", "last_name", "gender", "street", "city", "state", "zip"].forEach((k) => { if (f[k] && String(f[k]).trim()) body[k] = String(f[k]).trim(); });
    if (isCard) ["payer_name", "member_id", "group_number"].forEach((k) => { if (f[k] && String(f[k]).trim()) body[k] = String(f[k]).trim(); });
    body.dob = toISO(f.dob);
    try {
      const d = await addRosterPatient(clientId, body);
      const np = d?.patient || {};
      await assignPatient(analysisId, { mrn: np.mrn || null, full_name: np.full_name || `${f.first_name} ${f.last_name}`.trim(), dob: body.dob, source: "roster" });
      toast.success(`${displayName(np.full_name || `${f.first_name} ${f.last_name}`)} added to revQ · ${np.mrn || ""}`.trim());
      onDone?.();
    } catch (err) {
      setBusy(false);
      const st = err?.response?.status;
      const dd = err?.response?.data?.detail;
      if (st === 409 && dd && Array.isArray(dd.duplicates)) { setDupPanel(dd.duplicates); setPick(null); }
      else if (st === 400) setError("First name, last name and date of birth are needed to create a patient.");
      else setError("Couldn't create the patient. Try again.");
    }
  };

  const primaryLabel = pick ? `Link to ${displayName(pick.full_name || pick.name || "patient")}` : (busy ? "Creating…" : "Create patient & link");

  return (
    <div data-testid="pd-create-block">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[13px] font-semibold" style={{ color: T.ink }}>Create a patient from this document</span>
        <span className="flex-1" />
        <button type="button" onClick={onCancel} data-testid="pd-create-cancel" className="text-[12.5px] font-semibold" style={{ color: T.indigo }}>Search for an existing patient instead</button>
      </div>

      {dupPanel == null && dupes != null ? (
        dupes.length ? (
          <div className="mb-3">
            <div className="text-[12px] font-semibold mb-1.5" style={{ color: T.amberTxt }}><span style={{ color: T.amberDot }}>●</span> Check these first. They may already be in revQ.</div>
            <div role="radiogroup" className="space-y-1.5">{dupes.map((p) => <DupRow key={p.mrn || p.chart_id} p={p} active={pick && (pick.mrn || pick.chart_id) === (p.mrn || p.chart_id)} onPick={() => setPick(pick && (pick.mrn || pick.chart_id) === (p.mrn || p.chart_id) ? null : p)} testid={`pd-dupcheck-${p.mrn || p.chart_id}`} />)}</div>
          </div>
        ) : <div className="text-[12px] mb-3" style={{ color: T.mute }}>No patient in client {clientId} has this name or date of birth.</div>
      ) : null}

      <div className="grid gap-2.5 pd-create-grid" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}>
        <Field label="First name" req value={f.first_name} onChange={(v) => set("first_name", v)} testid="pd-create-first" autoFocus={focusFirst} />
        <Field label="Last name" req value={f.last_name} onChange={(v) => set("last_name", v)} testid="pd-create-last" />
        <Field label="Date of birth" req value={f.dob} onChange={(v) => set("dob", v)} testid="pd-create-dob" />
        <Field label="Sex" value={f.gender} onChange={(v) => set("gender", v)} testid="pd-create-sex" />
        <Field label="Street" value={f.street} onChange={(v) => set("street", v)} testid="pd-create-street" />
        <Field label="City" value={f.city} onChange={(v) => set("city", v)} testid="pd-create-city" />
        <Field label="State" value={f.state} onChange={(v) => set("state", v)} testid="pd-create-state" />
        <Field label="ZIP" value={f.zip} onChange={(v) => set("zip", v)} testid="pd-create-zip" />
        {isCard ? <>
          <Field label="Payer" value={f.payer_name} onChange={(v) => set("payer_name", v)} testid="pd-create-payer" w={2} />
          <Field label="Member ID" value={f.member_id} onChange={(v) => set("member_id", v)} testid="pd-create-member" />
          <Field label="Group" value={f.group_number} onChange={(v) => set("group_number", v)} testid="pd-create-group" />
        </> : null}
      </div>

      <div className="text-[12px] mt-2" style={{ color: T.mute }}>Fields come from what Lens read. revQ gives the patient the next MRN and links this document to them.</div>

      {dupPanel ? (
        <div className="mt-3 rounded-[10px] p-3" style={{ border: `1px solid ${T.amberDot}`, background: T.card }} data-testid="pd-create-dup-panel">
          <div className="text-[12px] font-semibold mb-2" style={{ color: T.amberTxt }}><span style={{ color: T.amberDot }}>●</span> Possible duplicate · A patient with this name and date of birth is already in revQ. Link this document to them instead.</div>
          <div role="radiogroup" className="space-y-1.5">{dupPanel.map((p) => <DupRow key={p.mrn} p={p} active={pick && pick.mrn === p.mrn} onPick={() => setPick(p)} testid={`pd-create-dup-${p.mrn}`} />)}</div>
        </div>
      ) : null}

      {error ? <div className="mt-2 flex items-center gap-2 text-[12px]" style={{ color: T.red }} data-testid="pd-create-error"><span style={{ width: 6, height: 6, borderRadius: 999, background: T.red }} />{error}</div> : null}

      <div className="flex items-center gap-2 mt-3">
        {onOpenDoc ? <button type="button" onClick={onOpenDoc} data-testid="needs-create-open-document" className="text-[12.5px] font-semibold" style={{ color: T.indigo }}>Open document</button> : null}
        <span className="flex-1" />
        <button type="button" onClick={submit} disabled={busy} data-testid="create-patient-submit" className="h-9 px-4 rounded-[9px] text-[12.5px] font-semibold text-white" style={{ background: T.ink }}>{primaryLabel}</button>
      </div>
    </div>
  );
}
